import React, { useEffect, useRef, useState } from "react";

import Card from 'react-bootstrap/Card';


import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Polyline from "@arcgis/core/geometry/Polyline";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from "@arcgis/core/widgets/Expand";
import Home from "@arcgis/core/widgets/Home";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import "@arcgis/core/assets/esri/themes/light/main.css";
import Bookmarks from "@arcgis/core/widgets/Bookmarks";
import Bookmark from "@arcgis/core/webmap/Bookmark";
import Locate from "@arcgis/core/widgets/Locate.js";
import Measurement from "@arcgis/core/widgets/Measurement.js";
import LabelClass from "@arcgis/core/layers/support/LabelClass.js";

import type { ObservationType } from "../types/types";
import { useAdminContext } from "@/contexts/AdminContext";



interface MapComponentProps {
  observationData: ObservationType[];
}

declare global {
  namespace __esri {
    interface MapView {
      ui: any;
      destroy: () => void;
    }
  }
}

const convertToTahiti = (utcString: string) => {

  if (utcString === null){
    return "Nécessite une mise à jour de TOHORA TURE"
  }

  const date = new Date(utcString + "Z");
  console.log("raw date", date)
  console.log(utcString)
  return date.toLocaleString('fr-FR', {timeZone:'Pacific/Tahiti'})
}


// Fonction pour initialiser la carte et la vue
const initializeMap = (container: HTMLDivElement): MapView => {
  const map = new Map({ basemap: "hybrid" });
  const view = new MapView({
    container,
    map,
    center: [-149.566, -17.5516],
    zoom: 10,
  });
  return view;
};

const initializeWidgets = (view: MapView): void => {
  
  const measurement = new Measurement({view: view});
  const measurementExpand = new Expand({view, content: measurement});
  view.ui.add(measurementExpand, "top-left");

  // Activer l'outil de mesure uniquement quand l'Expand est ouvert
  measurementExpand.watch("expanded", (isExpanded) => {
    if (isExpanded) {
      measurement.activeTool = "distance"; // Active la mesure de distance
    } else {
      measurement.clear(); // Désactive complètement la mesure
    }
  });

  const basemapGallery = new BasemapGallery({ view });
  const basemapExpand = new Expand({ view, content: basemapGallery });
  view.ui.add(basemapExpand, "top-left");

  const homeWidget = new Home({ view });
  view.ui.add(homeWidget, "top-left");


  const bookmarks = new Bookmarks({
    view,
    bookmarks: [
      new Bookmark({
        name: "Tahiti",
        viewpoint: {
          targetGeometry: new Point({
            longitude: -149.566,
            latitude: -17.5516,
          }),
          scale: 400000,
        },
      }),
      new Bookmark({
        name: "Nouméa", // Nouvelle-Calédonie
        viewpoint: {
          targetGeometry: new Point({
            longitude: 166.4423,
            latitude: -22.2758,
          }),
          scale: 50000,
        },
      })
    ],
  });
  const bookmarksExpand = new Expand({ view, content: bookmarks });
  view.ui.add(bookmarksExpand, "top-left");

  const locateWidget = new Locate({
    view: view,
    graphic: new Graphic({
      symbol: new SimpleMarkerSymbol({
        // Correction ici
        style: "circle",
        color: "blue",
        size: 8,
        outline: {
          color: "white",
          width: 2,
        },
      }),
    }),
  });
  view.ui.add(locateWidget, "top-left");

  // Ajouter FeatureTable dans le conteneur HTML
  /*
  const featureTable = new FeatureTable({
    view: view,
    layer: featureLayer,
    container: "tableDiv",
  });
  view.ui.add(featureTable, "bottom-right");
  */
};

const popupLine = `
        <table style="width:100%; border-collapse: collapse;">
          <tr>
            <td style="border: 1px solid black; padding: 5px;">ID</td>
            <td style="border: 1px solid black; padding: 5px;">{objectid}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Nom du bateau</td>
            <td style="border: 1px solid black; padding: 5px;">{nom_bateau}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Description</td>
            <td style="border: 1px solid black; padding: 5px;">{description}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Date de création</td>
            <td style="border: 1px solid black; padding: 5px;">{phone_create_date}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Dernière modification</td>
            <td style="border: 1px solid black; padding: 5px;">{phone_update_date}</td>
          </tr>
        </table>
      `;
  
const popupPoint = `
        <table style="width:100%; border-collapse: collapse;">
          <tr>
            <td style="border: 1px solid black; padding: 5px;">ID</td>
            <td style="border: 1px solid black; padding: 5px;">{objectid}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Nom du bateau</td>
            <td style="border: 1px solid black; padding: 5px;">{nom_bateau}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Description</td>
            <td style="border: 1px solid black; padding: 5px;">{description}</td>
          </tr>
          <tr>
            <td style="border: 1px solid black; padding: 5px;">Dernière modification</td>
            <td style="border: 1px solid black; padding: 5px;">{phone_update_date}</td>
          </tr>
        </table>
      `;

// Fonction pour initialiser les couches
const initializeLayers = (view: MapView): { pointLayer: FeatureLayer; lineLayer: FeatureLayer} => {


  const pointLayer = new FeatureLayer({
    source: [],
    objectIdField: "objectid",
    fields: [
      { name: "objectid", type: "oid" },
      { name: "nom_bateau", type: "string" },
      { name: "code_pf", type: "string" },
      { name: "description", type: "string" },
      { name: "phone_update_date", type: "string" }
    ],
    geometryType: "point",
    popupTemplate: {
      title: "Bateau : {nom_bateau}",
      content: popupPoint,
    },
  });

  const lineLayer = new FeatureLayer({
    source: [],
    objectIdField: "objectid",
    fields: [
      { name: "objectid", type: "oid" },
      { name: "nom_bateau", type: "string" },
      { name: "code_pf", type: "string" },
      { name: "description", type: "string" },
      { name: "phone_create_date", type: "string" },
      { name: "phone_update_date", type: "string" }
    ],
    geometryType: "polyline",
    popupTemplate: {
      title: "Bateau : {nom_bateau}",
      content: popupLine,
    },
  });

  const codePFLabelClass = new LabelClass({
    labelExpressionInfo: { expression: "$feature.code_pf" },
    symbol: {
      type: "text",  // autocasts as new TextSymbol()
      color: "white",
      haloSize: 1,
      haloColor: "black"
    }
  });

lineLayer.labelingInfo = [ codePFLabelClass ];

  
  view.map.addMany([pointLayer, lineLayer]);

  return { pointLayer, lineLayer };
};

const cleanLineLayer = (lineLayer: FeatureLayer) => {
  lineLayer.queryObjectIds().then(oids => {
    if(!oids || oids.length == 0) return;
    lineLayer.applyEdits({
      deleteFeatures:  oids.map((oid) => ({ objectId: oid }))
    }).catch((error) =>
      console.error("Erreur lors de la suppression des oids :", error)
    );
  })
  .catch(err => console.error("Erreur lors de la query des oids:", err))
}

// Fonction pour mettre à jour la couche de points
const updatePointLayer = (
  pointLayer: FeatureLayer,
  observationData: ObservationType[],
  tabIdBoats: {[key: string]: {name: string, code: string}},
): void => {

  pointLayer.queryFeatures().then((featureSet) => {
      const deleteFeatures = featureSet.features.map((feature) => ({
        objectId: feature.attributes.objectid,
      }));

      const pointGraphics = observationData.filter((obs) => obs.en_cours).map((obs) => {
          return new Graphic({
            geometry: new Point({
              longitude: obs.shape_last_position.coordinates[0],
              latitude: obs.shape_last_position.coordinates[1],
            }),
            attributes: {
              objectid: obs.objectid,
              nom_bateau: obs.nom_bateau,
              code_pf: tabIdBoats[obs.bateau_id]?.code,
              phone_update_date: convertToTahiti(obs.phone_update_date),
              description: obs.description,
            },
          });
      });

      pointLayer.applyEdits({
          deleteFeatures,
          addFeatures: pointGraphics,
      }).catch((error) =>
        console.error("Erreur lors de la mise à jour des points :", error)
      );
  }).catch((error) =>
      console.error("Erreur lors de la récupération des points existants :", error)
  );
};

// Fonction pour mettre à jour la couche de lignes
const updateLineLayer = (
  lineLayer: FeatureLayer,
  observationData: ObservationType[],
  tabIdBoats: {[key: string]: {name: string, code: string}}
): void => {

  cleanLineLayer(lineLayer);
  const lineGraphics = observationData.map((obs) => {
    return new Graphic({
      geometry: new Polyline({
        paths: obs.shape.coordinates.map((coord: [number, number]) => [coord[0], coord[1],]),
      }),
      attributes: {
        objectid: obs.objectid,
        nom_bateau: obs.nom_bateau,
        code_pf: tabIdBoats[obs.bateau_id]?.code,
        phone_create_date: convertToTahiti(obs.phone_create_date),
        phone_update_date: convertToTahiti(obs.phone_update_date),
        description: obs.description,
      },
    });
  });

  lineLayer.applyEdits({
      addFeatures: lineGraphics,
  }).catch((error) =>
    console.error("Erreur lors de la mise à jour des lignes :", error)
  );

};

export default function MapEsri({ observationData }: MapComponentProps) {


  const {colorMapCache, tabIdBoats} = useAdminContext();
  const mapRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const pointLayerRef = useRef<FeatureLayer | null>(null);
  const lineLayerRef = useRef<FeatureLayer | null>(null);


  // Au montage de la carte
  useEffect(() => {
    if (!mapRef.current) return;

    const view = initializeMap(mapRef.current);
    initializeWidgets(view); 
    viewRef.current = view;
    const { pointLayer, lineLayer } = initializeLayers(view);
    pointLayerRef.current = pointLayer;
    lineLayerRef.current = lineLayer;

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!viewRef.current  || !pointLayerRef.current) return;
    updatePointLayer(pointLayerRef.current, observationData, tabIdBoats);
  }, [observationData, tabIdBoats]);

  useEffect(() => {
    if (!viewRef.current || !lineLayerRef.current) return;
    updateLineLayer(lineLayerRef.current, observationData, tabIdBoats);
  }, [observationData, tabIdBoats]);

  // update du renderer
  useEffect(()=>{
    if(!lineLayerRef.current || !pointLayerRef.current) return;
    const lineRenderer = new UniqueValueRenderer({
                          field: "code_pf",
                          defaultSymbol: new SimpleLineSymbol({
                            color: "gray",
                            width: 2,
                          }),
                          uniqueValueInfos: Object.keys(colorMapCache).map((boatCode) => ({
                            value: boatCode,
                            symbol: new SimpleLineSymbol({
                              color: colorMapCache[boatCode],
                              width: 2,
                            }),
                          })),
                        });
    const pointRenderer = new UniqueValueRenderer({
                          field: "code_pf",
                          defaultSymbol: new SimpleMarkerSymbol({
                            color: "gray",
                            size: 12,
                            outline: { color: "white", width: 2 }
                          }),
                          uniqueValueInfos: Object.keys(colorMapCache).map((boatCode) => ({
                            value: boatCode,
                            symbol: new SimpleMarkerSymbol({
                            color: colorMapCache[boatCode],
                            size: 12,
                            outline: { color: "white", width: 2 }
                          }),
                          })),
                        });
   

    lineLayerRef.current.renderer = lineRenderer;
    pointLayerRef.current.renderer = pointRenderer;
  },[colorMapCache]);

  return (
      <Card className="left">
        <Card.Body>
          <div className="esri-map-container">
            <div ref={mapRef} className="esri-view"
              style={{ height: "700px", width: "100%" }}>
            </div>
          </div>
        </Card.Body>
      </Card>
  );
}
